import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import TextInput from "components/inputs/TextInput";
import styles from "../IndexRegistration.module.scss";
import { requestJson, RequestMethod } from "helpers/httpHelper";
import { toast } from "react-toastify";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const step1ValidationSchema = Yup.object().shape({
  customer_no: Yup.string().required("Cod client este obligatoriu"),
});

export default function Step1({ stepsState, persistState, setCurrentStep }) {
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [emailSent, setEmailSent] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: stepsState,
    validationSchema: step1ValidationSchema,
    onSubmit: async (values) => {
      if (!recaptchaToken) {
        toast.error("Vă rugăm să completați verificarea reCAPTCHA");
        return;
      }
      await saveData(values);
    },
  });

  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(cooldownSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  const startCooldown = () => {
    setCooldownSeconds(30);
  };

  const saveData = async (values) => {
    setIsLoading(true);
    try {
      const { response, isSuccess } = await requestJson(
        "account/validate-client-code",
        RequestMethod.POST,
        {
          data: {
            client_code: values.customer_no,
          },
        }
      );

      // Start cooldown timer regardless of response
      startCooldown();

      if (!isSuccess) {
        // Check if it's an invalid client code error
        if (response?.error) {
          formik.setFieldError("customer_no", "Codul client este invalid");
        }
        return;
      }

      // Check response status
      if (response?.success && response?.code === "MAIL_SENT") {
        setEmailSent(true);
        persistState((prevState) => ({
          ...prevState,
          step1: {
            customer_no: values.customer_no,
          },
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleBack = () => {
    window.location.href = "/";
  };

  const handleBackToForm = () => {
    setEmailSent(false);
    formik.setFieldValue("customer_no", "");
    persistState((prevState) => ({
      ...prevState,
      step1: {
        customer_no: "",
      },
    }));
  };

  if (emailSent) {
    return (
      <section className="pb-55">
        <div className={styles.indexContainer}>
          <div style={{ padding: "20px 0" }}>
            <h3 style={{ marginBottom: "20px", fontWeight: "600" }}>Email trimis cu succes!</h3>
            <p style={{ marginBottom: "15px", lineHeight: "1.6" }}>
              Vă rugăm să verificați adresa de email și să accesați linkul de validare.
              Linkul este valabil timp de 30 de minute.
            </p>
            <p style={{ marginBottom: "30px", lineHeight: "1.6" }}>
              Dacă nu ați primit emailul, verificați folderul spam sau încercați din nou.
            </p>
            <button
              type="button"
              onClick={handleBackToForm}
              className="btn btn-primary"
            >
              Înapoi la formular
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pb-55">
      <div className={styles.indexContainer}>
        <form onSubmit={formik.handleSubmit}>
            <div className={styles.formField}>
              <TextInput
                label="Cod client"
                type="number"
                name="customer_no"
                value={formik.values.customer_no}
                setFieldValue={formik.setFieldValue}
                error={formik.errors.customer_no}
                touched={formik.touched.customer_no}
                parentClassName="col-md-12"
                isDisabled={isLoading || cooldownSeconds > 0}
              />
            </div>

            <div className={styles.recaptchaContainer}>
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
              />
            </div>

            <div className="d-flex justify-content-between mt-4">
              <button
                type="button"
                onClick={handleBack}
                className="btn btn-danger"
                disabled={isLoading}
              >
                Înapoi
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={formik.isSubmitting || isLoading || cooldownSeconds > 0}
              >
                {isLoading ? "Se încarcă..." : cooldownSeconds > 0 ? `Așteaptă ${cooldownSeconds}s` : "Validează"}
              </button>
            </div>
          </form>
      </div>
    </section>
  );
}
